import React, {
  useState,
  useEffect,
  useCallback,
  useLayoutEffect,
} from "react";
import { StyleSheet, View, ScrollView, TouchableOpacity } from "react-native";
import { colors } from "src/assets";
import {
  BottomActions,
  Button,
  FormControlGroup,
  FormControlLabel,
  FormControlSwitch,
  Typography,
} from "src/components";
import { FormControlPrice, ImageCarousel } from "./components";
import { useNavigation, useRoute } from "@react-navigation/native";
import { EventRegister } from "react-native-event-listeners";
import { navigationRef, useOfferActions, useOffer } from "src/hooks";
import { useForm, useController } from "react-hook-form";
import * as formValidators from "src/utils/form-validator";
import { v4 as uuid } from "uuid";
import { categoryList, priceList } from "src/constant";
import LocalText from "src/local";
import { RootStackScreenProps } from "src/navigation/types";
import alert from "src/components/alert";

type ControlProps = {
  control: any;
  error?: string;
};

const NameControl: React.FC<ControlProps> = ({ control, error }) => {
  const { field } = useController({ control, defaultValue: "", name: "name" });
  const eventName = "OrderEditNameEvent";
  useEffect(() => {
    const listener = EventRegister.addEventListener(eventName, (v: string) => {
      field.onChange(v.trim());
    });
    return () => {
      EventRegister.removeEventListener(listener as string);
    };
  }, [field]);
  return (
    <FormControlLabel
      text={LocalText.screens.offerEdit.name}
      detail={field.value.length > 0 ? field.value : "(必須)"}
      error={error}
      rightIcon={"chevron"}
      onPress={() => {
        navigationRef.current?.navigate("CustomTextEdit", {
          eventName: eventName,
          title: LocalText.screens.offerEdit.name,
          placeholder: LocalText.screens.offerEdit.namePlaceholder,
          text: field.value,
        });
      }}
    />
  );
};

const CategoryControl: React.FC<ControlProps> = ({ control, error }) => {
  const { field } = useController({
    control,
    defaultValue: undefined,
    name: "category",
  });
  const eventName = "OrderEditCategoryEvent";
  useEffect(() => {
    const listener = EventRegister.addEventListener(eventName, (v: string) => {
      field.onChange(v);
    });
    return () => {
      EventRegister.removeEventListener(listener as string);
    };
  }, [field]);
  return (
    <FormControlLabel
      text={LocalText.screens.offerEdit.category}
      detail={
        categoryList.find((item) => item.key === field.value)?.label || "(必須)"
      }
      error={error}
      rightIcon={"chevron"}
      onPress={() => {
        navigationRef.current?.navigate("CustomSelectionList", {
          eventName: "OrderEditCategoryEvent",
          title: LocalText.screens.offerEdit.category,
          selectedId: field.value,
          items: categoryList.map((item: any) => ({
            id: item.key,
            label: item.label,
          })), // FIME: Remove any
        });
      }}
    />
  );
};

const PriceControl: React.FC<ControlProps> = ({ control, error }) => {
  const { field } = useController({
    control,
    defaultValue: undefined,
    name: "price",
  });
  const eventName = "OrderEditPriceEvent";
  useEffect(() => {
    const listener = EventRegister.addEventListener(eventName, (v: string) => {
      field.onChange(v);
    });
    return () => {
      EventRegister.removeEventListener(listener as string);
    };
  }, [field]);
  return (
    <FormControlPrice
      text={LocalText.screens.offerEdit.price}
      price={priceList.find((item) => item.key === field.value)?.label}
      priceSubItems={
        field.value
          ? [
              { title: "システム手数料", detail: "-20%", color: "gray" },
              {
                title: "合計",
                detail: `¥${Math.floor(parseInt(field.value) * 0.8)} / 1分`,
                color: "red",
              },
            ]
          : undefined
      }
      error={error}
      onPress={() => {
        navigationRef.current?.navigate("CustomSelectionList", {
          eventName: "OrderEditPriceEvent",
          title: LocalText.screens.offerEdit.price,
          selectedId: field.value,
          items: priceList.map((item) => ({
            id: item.key,
            label: item.label,
          })),
        });
      }}
    />
  );
};

const PublishControl: React.FC<ControlProps> = ({ control }) => {
  const { field } = useController({
    control,
    defaultValue: 0,
    name: "isPublish",
  });
  return (
    <FormControlSwitch
      text={LocalText.screens.offerEdit.publish}
      activeText={LocalText.screens.offerEdit.publishActive}
      inActiveText={LocalText.screens.offerEdit.publishInactive}
      value={field.value === 1}
      onChange={(value) => field.onChange(value ? 1 : 0)}
    />
  );
};

const DescriptionControl: React.FC<ControlProps> = ({ control, error }) => {
  const { field } = useController({
    control,
    defaultValue: "",
    name: "description",
  });
  const eventName = "OrderEditDescriptionEvent";
  useEffect(() => {
    const listener = EventRegister.addEventListener(eventName, (v: string) => {
      field.onChange(v.trim());
    });
    return () => {
      EventRegister.removeEventListener(listener as string);
    };
  }, [field]);
  return (
    <FormControlLabel
      text={LocalText.screens.offerEdit.description}
      detail={field.value.length > 0 ? field.value : "(必須)"}
      error={error}
      rightIcon={"chevron"}
      onPress={() => {
        navigationRef.current?.navigate("CustomTextEdit", {
          eventName: eventName,
          title: LocalText.screens.offerEdit.description,
          placeholder: LocalText.screens.offerEdit.description,
          text: field.value,
          isMultiline: true,
        });
      }}
    />
  );
};

export type OfferEditNavigationParam = {
  offerId?: string;
};

type FormValues = {
  images: string[];
  name: string;
  category: string | undefined;
  description: string;
  price: string | undefined;
  isPublish: number;
};

export async function copyTextToClipboard(text: string) {
  if ("clipboard" in navigator) {
    return await navigator.clipboard.writeText(text);
  } else {
    return document.execCommand("copy", true, text);
  }
}

export const OfferEditScreen: React.FC = () => {
  const navigation =
    useNavigation<RootStackScreenProps<"OfferEdit">["navigation"]>();
  const route = useRoute<RootStackScreenProps<"OfferEdit">["route"]>();
  const [offerId] = useState<string>(
    route.params.offerId || (uuid() as string)
  );
  const [offer, loading] = useOffer(route.params.offerId);
  const { uploadOfferImage, createOffer, updateOffer } = useOfferActions();
  const {
    register,
    control,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      images: [],
      name: "",
      description: "",
      category: undefined,
      price: "60",
      isPublish: 1,
    },
  });
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [, setError] = useState<Error | undefined>();

  const copyPublicUrl = useCallback(async () => {
    const origin =
      typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : "";
    const url = new URL(`/services/${offer?.id}`, origin).toString();
    await copyTextToClipboard(url);
    alert(
      "公開URLがコピーされました",
      undefined,
      [
        {
          text: LocalText.common.ok,
          onPress: () => {},
        },
      ],
      { cancelable: false }
    );
  }, [offer]);

  useLayoutEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <TouchableOpacity
          disabled={!offer || !offer.isPublic}
          style={{ marginRight: 16 }}
          onPress={copyPublicUrl}
        >
          <Typography
            text="公開URLをコピーする"
            textStyle={{
              color:
                !offer || !offer.isPublic ? colors.chokuGray : colors.chokuRed,
            }}
          />
        </TouchableOpacity>
      ),
    });
  }, [offer, copyPublicUrl, navigation]);

  useEffect(() => {
    if (offer) {
      setValue("images", offer.images || [], { shouldValidate: true });
      setValue("name", offer.title);
      setValue("description", offer.description || "");
      setValue("category", offer.category?.id);
      setValue("price", offer.price ? `${offer.price}` : undefined);
      setValue("isPublish", offer.isPublic || 0);
    }
  }, [offer, setValue]);
  useEffect(() => {
    register("images", {
      validate: (value) => {
        if (!value || value.length === 0) {
          return LocalText.screens.offerEdit.imagesValidationRequired;
        }
        return true;
      },
    });
    register("name", {
      validate: (value) => {
        if (!formValidators.RequireValidator(value)) {
          return LocalText.screens.offerEdit.nameValidationRequired;
        }
        return true;
      },
    });
    register("description", {
      validate: (value) => {
        if (!formValidators.RequireValidator(value)) {
          return LocalText.screens.offerEdit.descriptionValidationRequired;
        }
        return true;
      },
    });
    register("category", {
      validate: (value) => {
        if (!formValidators.RequireValidator(value)) {
          return LocalText.screens.offerEdit.categoryValidationRequired;
        }
        return true;
      },
    });
    register("price", {
      validate: (value) => {
        if (!formValidators.RequireValidator(value)) {
          return LocalText.screens.offerEdit.priceValidationRequired;
        }
        return true;
      },
    });
  }, [register]);

  const onAddImage = async (image: string) => {
    setValue(
      "images",
      [...(getValues().images || []), await uploadOfferImage(image, offerId)],
      {
        shouldValidate: true,
      }
    );
  };

  const onDeleteImage = useCallback(
    (index: number) => {
      setValue(
        "images",
        [...getValues().images.filter((item, i) => i !== index)],
        { shouldValidate: true }
      );
    },
    [setValue, getValues]
  );

  const onSave = async (data: FormValues) => {
    setIsProcessing(true);
    try {
      if (route.params.offerId === offerId) {
        await updateOffer({
          input: {
            id: offerId,
            title: data.name,
            description: data.description,
            price: data.price ? parseInt(data.price) : 0,
            isPublic: data.isPublish,
            categoryId: data.category || "",
            mainImageUrl: data.images[0],
            images: data.images,
          },
        });
      } else {
        await createOffer({
          input: {
            id: offerId,
            title: data.name,
            description: data.description,
            price: data.price ? parseInt(data.price) : 0,
            isPublic: data.isPublish,
            categoryId: data.category || "",
            mainImageUrl: data.images[0],
            images: data.images,
          },
        });
      }
      setIsProcessing(false);
      navigation.goBack();
    } catch (e) {
      console.error(e);
      setError(e as Error);
      setIsProcessing(false);
    }
  };

  return (
    <View style={styles.container}>
      {!loading ? (
        <React.Fragment>
          <ScrollView
            style={styles.scrollView}
            showsVerticalScrollIndicator={false}
          >
            <ImageCarousel
              style={styles.carousel}
              images={getValues().images}
              error={errors.images?.message}
              onAdd={onAddImage}
              onDelete={onDeleteImage}
            />
            <Typography
              text={LocalText.screens.offerEdit.serviceSection}
              variant={"section"}
            />
            <FormControlGroup>
              <NameControl control={control} error={errors.name?.message} />
              <CategoryControl
                control={control}
                error={errors.category?.message}
              />
              <PriceControl control={control} error={errors.price?.message} />
            </FormControlGroup>
            <FormControlGroup>
              <PublishControl
                control={control}
                error={errors.isPublish?.message}
              />
            </FormControlGroup>
            <Typography
              text={LocalText.screens.offerEdit.descriptionSection}
              variant={"section"}
            />
            <FormControlGroup>
              <DescriptionControl
                control={control}
                error={errors.description?.message}
              />
            </FormControlGroup>
          </ScrollView>
          <BottomActions>
            <Button
              variant={"secondary"}
              icon={"cancel"}
              iconColor={colors.chokuRed}
              title={LocalText.common.cancel}
              onPress={() => navigation.goBack()}
            />
            <Button
              variant={"primary"}
              icon={"check"}
              title={LocalText.common.done}
              loading={isProcessing}
              onPress={handleSubmit(onSave)}
            />
          </BottomActions>
        </React.Fragment>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: "column",
    backgroundColor: colors.white,
  },
  scrollView: {
    flex: 1,
    backgroundColor: colors.gray1,
  },
  carousel: {
    marginTop: 16,
    marginLeft: 8,
    marginBottom: 32,
  },
});
